/**
 * Akten-Tabelle Stufe 2 — Spalten-Berechnung + Sortier-Logik (Issue #652)
 *
 * Enthält reine Funktionen für berechnete Spalten (Rechnung offen seit,
 * Fällig seit, Countdown-Pill) und Sortier-Zustand-Management.
 * Keine React-Abhängigkeiten — alles unit-testbar.
 */

import type { BetriebId } from './betriebe';
import type { AktenSortierung } from './akten-stellungnahme';

// ─── Typen ───────────────────────────────────────────────────────────────────

export type CountdownLevel = 'gruen' | 'heute' | 'ueberfaellig';

export interface CountdownPill {
  level: CountdownLevel;
  tage: number;
}

export type SortDirection = 'asc' | 'desc' | null;

export interface AktenSortState {
  column: string | null;
  direction: SortDirection;
}

export type QuickSortKey =
  | 'hoechste_betraege'
  | 'laengste_offen'
  | 'hoechste_mahnstufe'
  | 'naechste_frist';

// ─── Hilfsfunktion: ISO-Datum auf UTC-Mitternacht parsen ──────────────────────

function parseIsoDate(isoDate: string): Date {
  // "YYYY-MM-DD" wird als UTC geparst; "YYYY-MM-DDTHH:mm..." bleibt unverändert.
  return new Date(isoDate.length === 10 ? isoDate + 'T00:00:00Z' : isoDate);
}

function diffTage(von: Date, bis: Date): number {
  const vonMidnight = Date.UTC(von.getUTCFullYear(), von.getUTCMonth(), von.getUTCDate());
  const bisMidnight = Date.UTC(bis.getUTCFullYear(), bis.getUTCMonth(), bis.getUTCDate());
  return Math.round((bisMidnight - vonMidnight) / (1000 * 60 * 60 * 24));
}

// ─── berechneRechnungOffenSeit ────────────────────────────────────────────────

/**
 * Tage seit Rechnungsdatum, nur wenn zahlungsstatus = 'offen'.
 * Gibt null zurück wenn kein Datum oder Status nicht 'offen'.
 */
export function berechneRechnungOffenSeit(
  rechnungDatum: string | null | undefined,
  zahlungsstatus: string | null | undefined,
  today: Date = new Date(),
): number | null {
  if (!rechnungDatum) return null;
  if (zahlungsstatus !== 'offen') return null;
  const rDatum = parseIsoDate(rechnungDatum);
  if (isNaN(rDatum.getTime())) return null;
  return diffTage(rDatum, today);
}

// ─── berechneFaelligSeit ──────────────────────────────────────────────────────

/** Standardmäßiges Zahlungsziel in Tagen. */
const ZAHLUNGSZIEL_TAGE = 30;

/**
 * Tage seit Fälligkeit (Rechnungsdatum + 30 Tage).
 * Negativ = noch nicht fällig. Null wenn kein Rechnungsdatum.
 */
export function berechneFaelligSeit(
  rechnungDatum: string | null | undefined,
  today: Date = new Date(),
): number | null {
  if (!rechnungDatum) return null;
  const rDatum = parseIsoDate(rechnungDatum);
  if (isNaN(rDatum.getTime())) return null;
  const faelligAm = new Date(rDatum);
  faelligAm.setUTCDate(faelligAm.getUTCDate() + ZAHLUNGSZIEL_TAGE);
  return diffTage(faelligAm, today);
}

// ─── berechneCountdownPill ────────────────────────────────────────────────────

/**
 * Countdown-Pill für Fristen (Nächste Frist Spalte).
 *   gruen       : Datum in der Zukunft (tage > 0)
 *   heute       : Datum ist heute (tage = 0)
 *   ueberfaellig: Datum in der Vergangenheit (tage < 0)
 */
export function berechneCountdownPill(
  faelligAm: string | null | undefined,
  today: Date = new Date(),
): CountdownPill | null {
  if (!faelligAm) return null;
  const frist = parseIsoDate(faelligAm);
  if (isNaN(frist.getTime())) return null;
  const tage = diffTage(today, frist);
  let level: CountdownLevel;
  if (tage > 0) level = 'gruen';
  else if (tage === 0) level = 'heute';
  else level = 'ueberfaellig';
  return { level, tage };
}

// ─── getDefaultSort ───────────────────────────────────────────────────────────

/**
 * Default-Sortierung pro Betrieb.
 * Gutachten/Autoglas: Alter desc (created_at)
 * Mietwagen:         Mietbeginn desc
 * Werkstatt:         Auftragsdatum desc
 */
export function getDefaultSort(betriebId: string): AktenSortierung {
  switch (betriebId as BetriebId) {
    case 'mietwagen':
      return { column: 'mietbeginn', ascending: false, nullsFirst: false };
    case 'werkstatt':
      return { column: 'auftragsdatum', ascending: false, nullsFirst: false };
    default:
      return { column: 'created_at', ascending: false, nullsFirst: false };
  }
}

// ─── applyQuickSort ───────────────────────────────────────────────────────────

/**
 * Liefert die AktenSortierung für einen Quick-Sort-Button.
 * 'naechste_frist' kann nicht server-seitig sortiert werden (kein DB-Feld),
 * daher Fallback auf created_at asc (älteste zuerst = längste Wartezeit).
 */
export function applyQuickSort(key: QuickSortKey): AktenSortierung {
  switch (key) {
    case 'hoechste_betraege':
      return { column: 'gesamtbetrag', ascending: false, nullsFirst: false };
    case 'laengste_offen':
      return { column: 'rechnung_datum', ascending: true, nullsFirst: false };
    case 'hoechste_mahnstufe':
      return { column: 'forderung_stage', ascending: false, nullsFirst: false };
    case 'naechste_frist':
      return { column: 'created_at', ascending: true, nullsFirst: false };
  }
}

// ─── toggleColumnSort ─────────────────────────────────────────────────────────

/**
 * 3-State-Toggle für Spalten-Header: none → asc → desc → none.
 */
export function toggleColumnSort(
  current: AktenSortState,
  column: string,
): AktenSortState {
  if (current.column !== column) {
    return { column, direction: 'asc' };
  }
  if (current.direction === 'asc') return { column, direction: 'desc' };
  if (current.direction === 'desc') return { column: null, direction: null };
  return { column, direction: 'asc' };
}

// ─── sortStateToAktenSortierung ───────────────────────────────────────────────

// Virtual column slugs that don't exist in DB — mapped to real DB columns for sorting.
const VIRTUAL_COLUMN_MAP: Record<string, string> = {
  faellig_seit: 'rechnung_datum',
};

/**
 * Wandelt AktenSortState in AktenSortierung um (für useAktenPaginated).
 * Virtuelle Spalten-Keys (z.B. 'faellig_seit') werden auf DB-Spalten gemappt.
 * Gibt null zurück wenn kein Sort aktiv.
 */
export function sortStateToAktenSortierung(
  state: AktenSortState,
): AktenSortierung | null {
  if (!state.column || !state.direction) return null;
  const dbColumn = VIRTUAL_COLUMN_MAP[state.column] ?? state.column;
  return {
    column: dbColumn,
    ascending: state.direction === 'asc',
    nullsFirst: false,
  };
}
